import { useQuery } from '@tanstack/react-query';
import { ndfApi } from '../../services/ndf-api';
import { dateFr } from './ui/Champ';
import { MessageErreur } from './ui/MessageEtat';

/**
 * Chronologie d'une note de frais (R-HIS-01).
 *
 * Elle répond à une question qu'aucun autre écran ne traite : « que s'est-il
 * passé, et dans quel ordre ? » — utile quand une note revient refusée, quand
 * un remboursement tarde, ou quand quelqu'un conteste une correction.
 *
 * Groupée par jour, du plus récent au plus ancien ; à l'intérieur d'une
 * journée, l'ordre chronologique — on lit une journée dans le sens où elle
 * s'est déroulée.
 */
export function ChronologieNote({ noteId }: { noteId: string }) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['ndf', 'chronologie', noteId],
    queryFn: () => ndfApi.historique.note(noteId),
    retry: false,
  });

  if (isLoading) return <p className="text-sm text-nuit/60">Chargement…</p>;
  if (error) return <MessageErreur erreur={error} />;
  if (!data || data.total === 0) {
    return <p className="text-sm text-nuit/70">Aucun événement enregistré sur cette note.</p>;
  }

  return (
    <div className="space-y-4">
      {data.jours.map((j) => (
        <section key={j.jour}>
          <h3 className="mb-2 font-titre text-sm font-bold text-nuit">{dateFr(j.jour)}</h3>
          <ul className="space-y-2 border-l-2 border-nuit/10 pl-4">
            {j.evenements.map((e, i) => (
              <li key={i} className="relative">
                {/* Le point sur la ligne de temps : il donne la lecture
                    verticale, sans quoi la liste redevient un tableau. */}
                <span
                  className={`absolute -left-[21px] top-1.5 block h-2 w-2 rounded-full ${
                    e.ton === 'positif'
                      ? 'bg-vert-fond'
                      : e.ton === 'alerte'
                        ? 'bg-corail-fond'
                        : 'bg-nuit/30'
                  }`}
                />
                <p className="text-sm text-nuit">
                  <span className="font-medium">{e.quoi}</span>
                  <span className="text-nuit/60">
                    {' '}
 : {e.qui} à {new Date(e.quand).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                  </span>
                </p>
                {e.precision && <p className="text-sm text-nuit/70">{e.precision}</p>}
              </li>
            ))}
          </ul>
        </section>
      ))}
    </div>
  );
}
